import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getApiBaseUrl } from './apiBaseUrl';

const API_URL = getApiBaseUrl();

const bitbucketApi = axios.create({
  baseURL: `${API_URL}/bitbucket/admin`,
  headers: {
    'Content-Type': 'application/json'
  }
});

bitbucketApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useBitbucketConfig = () => {
  return useQuery({
    queryKey: ['bitbucket-config'],
    queryFn: async () => {
      const { data } = await bitbucketApi.get('/config');
      return data;
    }
  });
};

export const useConfigureWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ workspace_slug, access_token }) => {
      const { data } = await bitbucketApi.post('/configure', {
        workspace_slug,
        access_token
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitbucket-config'] });
    }
  });
};

export const useUpdateConfig = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (config) => {
      const { data } = await bitbucketApi.put('/config', config);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitbucket-config'] });
    }
  });
};

export const useDisconnectWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const { data } = await bitbucketApi.delete('/disconnect');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitbucket-config'] });
      queryClient.invalidateQueries({ queryKey: ['bitbucket-developers'] });
      queryClient.invalidateQueries({ queryKey: ['bitbucket-repositories'] });
    }
  });
};

export const useDiscoverWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const { data } = await bitbucketApi.post('/discover');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitbucket-developers'] });
      queryClient.invalidateQueries({ queryKey: ['bitbucket-repositories'] });
    }
  });
};

export const useSyncWorkspace = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const { data } = await bitbucketApi.post('/sync');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitbucket-config'] });
      queryClient.invalidateQueries({ queryKey: ['sync-status'] });
    }
  });
};

export const useSyncStatus = () => {
  return useQuery({
    queryKey: ['sync-status'],
    queryFn: async () => {
      const { data } = await bitbucketApi.get('/sync-status');
      return data;
    },
    refetchInterval: 30000
  });
};

export const useBitbucketDevelopers = (mapped) => {
  return useQuery({
    queryKey: ['bitbucket-developers', mapped],
    queryFn: async () => {
      const params = mapped !== undefined ? { mapped } : {};
      const { data } = await bitbucketApi.get('/developers', { params });
      return data;
    }
  });
};

export const useMapDeveloper = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ bitbucket_developer_id, user_id }) => {
      const { data } = await bitbucketApi.post('/map-developer', {
        bitbucket_developer_id,
        user_id
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitbucket-developers'] });
    }
  });
};

export const useBitbucketRepositories = () => {
  return useQuery({
    queryKey: ['bitbucket-repositories'],
    queryFn: async () => {
      const { data } = await bitbucketApi.get('/repositories');
      return data;
    }
  });
};

export const useMapRepository = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ repo_id, project_id }) => {
      const { data } = await bitbucketApi.post('/map-repository', {
        repo_id,
        project_id
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitbucket-repositories'] });
      queryClient.invalidateQueries({ queryKey: ['repository-mappings'] });
    }
  });
};

export const useRepositoryMappings = () => {
  return useQuery({
    queryKey: ['repository-mappings'],
    queryFn: async () => {
      const { data } = await bitbucketApi.get('/mappings');
      return data;
    }
  });
};

export const useDeleteMapping = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (mappingId) => {
      const { data } = await bitbucketApi.delete(`/mapping/${mappingId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repository-mappings'] });
      queryClient.invalidateQueries({ queryKey: ['bitbucket-repositories'] });
    }
  });
};
